import { APIDetails } from './apis-details';

export const personalizedLearningApi: APIDetails = {
  id: 'API_KEYS.personalizedLearningApi',
  title: 'Personalized Learning API',
  subtitle:
    'AI-powered Personalized Learning Journeys – From goal setting to structured micro and macro roadmaps.',
  description: [
    'Learners often struggle to plan a clear learning path aligned with their skills, goals, and preferences. Traditional course recommendations are generic, lack personalization, and fail to bridge knowledge gaps effectively.',
    '',
    'The Personalized Learning API addresses this by generating AI-driven learning journeys. Using advanced LLMs with AI, the API creates:',
    '• Micro roadmaps – daily tasks and short-term objectives',
    '• Macro roadmaps – long-term milestones, phases, and recommended resources',
    '• Automated progress tracking, feedback, and skill gap analysis',
    '',
    'This allows platforms to personalize learning experiences at scale, enabling learners to follow structured, goal-oriented learning paths efficiently.',
  ],
  overview: [
    'The Personalized Journey API provides endpoints for:',
    '• Creating, retrieving, and deleting personalized learning journeys',
    '• Fetching micro-learning and macro-learning roadmaps',
    '• Automating AI-driven analysis for strengths, gaps, and recommendations',
  ],
  features: [
    {
      title: 'Create Learning Journey',
      description:
        'AI-generated curriculum based on learner goals and preferences',
    },
    {
      title: 'Delete Learning Journey',
      description: 'Permanently remove journey and progress data',
    },
    {
      title: 'Micro Roadmap',
      description:
        'Daily tasks, short-term learning objectives, skill gaps, and practice exercises',
    },
    {
      title: 'Macro Roadmap',
      description:
        'Comprehensive phased learning plan with durations, milestones, resources, and projects',
    },
    {
      title: 'Resource Recommendations',
      description: 'Courses, blogs, books for guided learning',
    },
    {
      title: 'Career Guidance',
      description:
        'Next steps, certifications, networking, and portfolio recommendations',
    },
  ],
  workflow: [],
  endpoints: [
    {
      category: 'Personalized Learning Journey',
      items: [
        {
          category: 'Create Learning Journey',
          method: 'POST',
          path: '/api/v1/PersonalizedJourney/Create',
          description:
            'Creates a new learning journey and starts AI curriculum generation',
        },
        {
          category: 'Get Micro Roadmap',
          method: 'GET',
          path: '/api/v1/PersonalizedJourney/Micro/{journey_id}',
          description:
            'Retrieves detailed micro-learning roadmap with daily tasks and short-term goals',
        },
        {
          category: 'Get Macro Roadmap',
          method: 'GET',
          path: '/api/v1/PersonalizedJourney/Macro/{journey_id}',
          description:
            'Retrieves macro-learning roadmap with phases, durations, milestones, and resources',
        },
        {
          category: 'Delete Learning Journey',
          method: 'DELETE',
          path: '/api/v1/PersonalizedJourney/Delete',
          description:
            'Deletes a learning journey and all associated data permanently',
        },
      ],
    },
  ],
  apiDocumentation: [
    {
      title: 'Create Learning Journey',
      method: 'POST',
      path: '/api/v1/PersonalizedJourney/Create',
      requestPath: 'POST /api/v1/PersonalizedJourney/Create',
      description:
        'Create a new Personalized Learning Journey and start background analysis',
      requestExample: {
        Description:
          'Starting from zero and learning step-by-step to become a DevOps Engineer who can deploy applications, automate tasks, and work with basic cloud tools.',
        Preference: [
          'Learn tools first, then practice with small projects',
          'Simple and easy explanations for every DevOps topic',
          'Hands-on practice with beginner-friendly tasks',
        ],
        Skills: [
          'Basic computer skills',
          'Basic Git and GitHub knowledge',
          'Basic Linux commands (cd, ls, mkdir)',
        ],
        Goal: 'Become a beginner-to-job-ready DevOps Engineer',
        Background: {
          Education: 'Computer Science Graduate',
          WorkExperience: 'No DevOps work experience',
        },
      },
      responseExample: {
        Journey_id: '436d0218-1eac-4195-96ba-848d30fd09db',
        Success: true,
        Status: 'pending',
        Message: 'Journey created successfully. Analysis started.',
        CreatedAt: '2025-11-26T13:40:58.975313',
      },
      statusCode: '202',
    },
    {
      title: 'Get Micro Roadmap',
      method: 'GET',
      path: '/api/v1/PersonalizedJourney/Micro/{journey_id}',
      requestPath: 'GET /api/v1/PersonalizedJourney/Micro/{journey_id}',
      description:
        'Returns short-term roadmap with daily tasks, strengths, gaps, and practice exercises',
      responseExample: {
        Id: 'faebbe72-0b67-4b1a-98be-bc343c630799',
        Status: 'complete',
        RoadmapTitle: 'DevOps Fundamentals: Understanding CI/CD Pipelines',
        CurrentAnalysis: {
          strengths: [
            'Computer Science Graduate',
            'Basic Git and GitHub knowledge',
            'Basic Linux commands',
            'Strong foundational computer skills',
          ],
          gaps: [
            'No practical DevOps work experience',
            'Lack of specific tool knowledge (CI/CD, cloud, automation)',
          ],
        },
        CoreFocus:
          'Introduce the core concepts of DevOps culture and CI/CD pipelines',
        RequiredKnowledge: [
          'Basic Git and GitHub knowledge',
          'Basic Linux commands',
        ],
        ConceptsToUnderstand: [
          { Topic: 'What is DevOps?', Summary: '...', BasicPrimer: '...' },
          {
            Topic: 'Introduction to CI/CD',
            Summary: '...',
            BasicPrimer: '...',
          },
          {
            Topic: 'The CI/CD Pipeline Stages',
            Summary: '...',
            BasicPrimer: '...',
          },
        ],
        Practice: [
          'Diagram a Simple CI/CD Pipeline',
          'Git Workflow Simulation',
          'Identify Automation Benefits',
        ],
        RecommendedResources: {
          courses: [
            {
              title: 'Introduction to DevOps',
              url: 'https://www.coursera.org/learn/introduction-to-devops',
            },
          ],
          blogs: [
            {
              title: "What is DevOps? A Beginner's Guide",
              url: 'https://www.atlassian.com/devops/what-is-devops',
            },
          ],
          books: [],
        },
        CareerGuidanceAndNextSteps: [
          'Begin exploring a specific CI/CD tool',
          'Start learning fundamentals of a cloud provider',
        ],
      },
      statusCode: '200',
    },
    {
      title: 'Get Macro Roadmap',
      method: 'GET',
      path: '/api/v1/PersonalizedJourney/Macro/{journey_id}',
      requestPath: 'GET /api/v1/PersonalizedJourney/Macro/{journey_id}',
      description:
        'Returns full learning roadmap – phases, durations, milestones, resources',
      responseExample: {
        Id: 'b878c4ba-63f4-44b3-91d3-b1e9f408f9ba',
        Status: 'complete',
        RoadmapTitle:
          'DevOps Engineer Macro Roadmap: From Zero to Job-Ready (14 Months)',
        CurrentAnalysis: {
          /* detailed analysis object */
        },
        Phases: [
          {
            Title: 'Phase 1: Linux, Scripting & Networking Fundamentals',
            Duration: '3 Months',
            Module: [
              'Advanced Linux Administration',
              'Shell Scripting',
              'Python for Automation',
              'Networking Essentials',
              'Virtualization Introduction',
            ],
            PhaseDescription: '...',
            KeyMilestone: '...',
            RequiredSkills: [
              'Advanced Linux CLI',
              'Bash scripting',
              'Python scripting',
              'Networking fundamentals',
            ],
            PracticeTopics: [
              'Setting up Linux server',
              'Writing automation scripts',
              'Troubleshooting network issues',
            ],
            RecommendedResources: {
              /* resources object */
            },
          },
          {
            Title: 'Phase 2: Containerization & Version Control Mastery',
            /* ... */
          },
          {
            Title: 'Phase 3: Cloud & Infrastructure as Code',
            /* ... */
          },
          {
            Title:
              'Phase 4: Advanced CI/CD, Configuration & Project Integration',
            /* ... */
          },
        ],
        CareerGuidanceAndNextSteps: [
          'Build a strong portfolio',
          'Certifications',
          'Networking',
          'Interview preparation',
          'Resume optimization',
          'Continuous learning',
        ],
      },
      statusCode: '200',
    },
    {
      title: 'Delete Learning Journey',
      method: 'DELETE',
      path: '/api/v1/PersonalizedJourney/Delete',
      requestPath: 'DELETE /api/v1/PersonalizedJourney/Delete',
      description:
        'Deletes a learning journey and all associated data permanently',
      requestExample: {
        journey_id: '436d0218-1eac-4195-96ba-848d30fd09db',
      },
      responseExample: {
        success: true,
        message: 'Journey deleted successfully',
      },
      statusCode: '200',
    },
  ],
  useCases: [
    {
      title: 'Personalized Learning Path Creation',
      description:
        'Users provide goals, skills, and preferences. API generates tailored micro and macro roadmaps.',
    },
    {
      title: 'Skill Gap Analysis & Recommendations',
      description:
        'Identify learners’ strengths and gaps, suggest courses, books, blogs, and hands-on exercises.',
    },
    {
      title: 'Corporate Upskilling & Employee Training',
      description:
        'HR teams can assign learning journeys to employees and track progress.',
    },
    {
      title: 'Portfolio & Career Guidance',
      description:
        'API suggests next steps, certifications, and projects for job readiness.',
    },
    {
      title: 'Gamified Learning & Progress Tracking',
      description:
        'Platforms can show daily tasks and milestones, enabling learner motivation and retention.',
    },
    {
      title: 'Integration with LMS / EdTech Platforms',
      description:
        'API can be integrated to automatically generate learning content and curriculum for students or users.',
    },
  ],
  targetUsers: [
    'E-learning Platforms – Provide AI-driven personalized learning journeys to users.',
    'HR & Corporate Training Teams – Offer structured upskilling paths to employees.',
    'Individual Learners – Plan and follow goal-oriented self-learning journeys.',
    'Educational Institutions – Integrate personalized learning plans into their curriculum.',
    'Coaching & Mentorship Services – Track progress and provide personalized recommendations.',
  ],
};
